/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from "react";
import styles from "./FooterSection.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
import AnimatedSection from "../AnimatedSection/AnimatedSection";
import { currentLanguage } from "../../i18n";

import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { t } from "../../i18n";

interface FooterSectionProps {
  missionRef: React.RefObject<HTMLDivElement | null>;
  pressRef: React.RefObject<HTMLDivElement | null>;
  testimonialRef: React.RefObject<HTMLDivElement | null>;
  blogRef: React.RefObject<HTMLDivElement | null>;
  showcaseRef: React.RefObject<HTMLDivElement | null>;
  solutionsRef: React.RefObject<HTMLDivElement | null>;
  contactRef: React.RefObject<HTMLDivElement | null>;
}

const FooterSection: React.FC<FooterSectionProps> = ({ missionRef, pressRef, testimonialRef, blogRef, showcaseRef, solutionsRef, contactRef }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Sayfa yüklendiğinde hash'e göre otomatik scroll
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.startsWith("#")) {
      const sectionId = hash.substring(1);

      // Contact section için özel işlem
      if (sectionId === "contact") {
        setTimeout(() => {
          let targetElement = (document.querySelector("[data-contact-section]") as HTMLElement | null) || document.getElementById("contact");

          if (targetElement) {
            const headerOffset = 100;
            const elementPosition = targetElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        }, 500);
      } else {
        const targetElement = document.getElementById(sectionId);
        if (targetElement) {
          const headerOffset = 80;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }
    }
  }, [location.hash]);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, ref: React.RefObject<HTMLDivElement | null>, sectionId?: string) => {
    e.preventDefault();
    const element = ref.current;
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      if (sectionId) {
        const newPath = `/${currentLanguage}/#${sectionId}`;
        window.history.pushState(null, "", newPath);
      }
    } else {
      const hash = sectionId ? `#${sectionId}` : "";
      navigate(`/${currentLanguage}/${hash}`);
      setTimeout(() => {
        const contactElement = document.querySelector("[data-contact-section]") as HTMLElement | null;
        if (contactElement) {
          const headerOffset = 100;
          const elementPosition = contactElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 300);
    }
  };

  return (
    <footer className={styles.footer}>
      <AnimatedSection delay={200}>
        {/* Footer içeriği */}
        <div className={styles.topRow}>
          {/* 1) Globe */}
          <div className={styles.mapCol}>
            <img
              src={currentLanguage === "en" ? "/images/globeIstanbulEN.png" : "/images/globeIstanbul2.png"}
              alt={currentLanguage === "en" ? "Greetings from Istanbul" : "Istanbul'dan Sevgilerle"}
              className={styles.mapImg}
              tabIndex={0}
            />
          </div>

          <div className={`${styles.addressCol} ${styles.open}`} aria-hidden={false}>
            <div className={styles.addressContent}>
              <div className={styles.colTitle}>{t("footer.address")}</div>
              <div className={styles.addressLines}>
                <ul>
                  <li>
                    <div>YTÜ Yıldız Teknopark</div>
                    <div>Esenler, İstanbul</div>
                  </li>
                </ul>
                <ul>
                  <li>
                    <div>Maslak</div>
                    <div>İstanbul</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className={styles.linksCol}>
            <div className={styles.linksGrid}>
              <div>
                <div className={styles.colTitle}>{t("footer.about")}</div>
                <a href={`/${currentLanguage}/our-mission`}>{t("footer.mission")}</a>
                <a href={`/${currentLanguage}/#contact`} onClick={(e) => scrollToSection(e, contactRef, "contact")}>
                  {t("footer.talkToOurTeam")}
                </a>
                <a href={`/${currentLanguage}/our-mission`}>{t("footer.getToKnowUs")}</a>
                <a href={`/${currentLanguage}/documents`}>{t("footer.documents")}</a>
                <a href={`/${currentLanguage}/documents`}>{t("footer.cookies")}</a>
              </div>
              <div>
                <div className={styles.colTitle}>{t("footer.aboutUs")}</div>
                <a href={`/${currentLanguage}/blog`}>{t("footer.blog")}</a>
                <a href={`/${currentLanguage}/#contact`} onClick={(e) => scrollToSection(e, contactRef, "contact")}>
                  {t("footer.requestDemo")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={400}>
        <div className="flex flex-col px-12 gap-4 mb-10">
          <div className="flex gap-4 text-3xl">
            <a href="https://www.instagram.com/senswise/" target="_blank" rel="noopener noreferrer" className="hover:text-primaryPurple-light transition-colors">
              <InstagramIcon fontSize="inherit" />
            </a>
            <a
              href="https://www.youtube.com/channel/UCI_48_AyrKBbqZtLL7XOTNg"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primaryPurple-light transition-colors"
            >
              <YouTubeIcon fontSize="inherit" />
            </a>
            <a
              href="https://www.linkedin.com/company/senswiseco/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primaryPurple-light transition-colors"
            >
              <LinkedInIcon fontSize="inherit" />
            </a>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={600}>
        <div className={styles.bottomRow}>
          <span className={styles.copyright}>
            © {new Date().getFullYear()} {t("footer.copyright")}
          </span>
          <br />
          <span>{t("footer.allRightsReserved")}</span>
        </div>
      </AnimatedSection>
    </footer>
  );
};

export default FooterSection;
